/**
 * FreightAuditDialog — bulk "Check Freight Charges" for B2B.
 *
 * Flow: pick a date window (+ optional seller) → SCAN re-pulls live Delhivery freight for
 * delivered/return-delivered orders and flags the ones whose seller charge drifted → the
 * admin selects some/all → FIX applies the per-order freight-refresh (atomic +/- wallet
 * adjustment, exactly the amount reviewed). Both phases are async jobs polled every 3s
 * (reuses the bulk_operation_history job doc + GET /bulk-operations/history/{id}).
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, AlertTriangle, CheckCircle2, Search } from "lucide-react";

const money = (v) =>
  v == null ? "0.00" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const isoDay = (d) => d.toISOString().slice(0, 10);
const today = () => isoDay(new Date());
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };

function ProgressBar({ done, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: "#00A699" }} />
      </div>
      <div className="mt-1 text-xs text-slate-500 tabular-nums">{done} / {total} ({pct}%)</div>
    </div>
  );
}

export default function FreightAuditDialog({ open, onOpenChange }) {
  const [phase, setPhase] = useState("config"); // config | scanning | results | fixing | done
  const [from, setFrom] = useState(daysAgo(90));
  const [to, setTo] = useState(today());
  const [sellerId, setSellerId] = useState("");
  const [count, setCount] = useState(null);     // {count, cap, over_cap}
  const [starting, setStarting] = useState(false);
  const [auditId, setAuditId] = useState(null);
  const [audit, setAudit] = useState(null);     // scan poll doc
  const [selected, setSelected] = useState(() => new Set());
  const [fixDoc, setFixDoc] = useState(null);   // fix poll doc
  const [fixId, setFixId] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const pollJob = (id, onTerminal) => {
    stopPolling();
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/bulk-operations/history/${id}`);
        onTerminal(data, false);
        if (data.status === "completed" || data.status === "failed") {
          stopPolling();
          onTerminal(data, true);
        }
      } catch { /* keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  const onScanTick = (doc, terminal) => {
    setAudit(doc);
    if (terminal) {
      if (doc.status === "failed") { toast.error("Scan failed"); setPhase("config"); }
      else {
        setSelected(new Set((doc.discrepancies || []).map((d) => d.order_id)));
        setPhase("results");
      }
    }
  };

  const onFixTick = (doc, terminal) => {
    setFixDoc(doc);
    if (terminal) {
      setPhase("done");
      toast.success(`Fixed ${doc.successful_orders || 0}, skipped ${doc.skipped_orders || 0}, failed ${doc.failed_orders || 0}`);
    }
  };

  const resetToConfig = () => {
    stopPolling();
    setPhase("config"); setAuditId(null); setAudit(null); setSelected(new Set());
    setFixDoc(null); setFixId(null);
  };

  // The component stays mounted while closed, so its state persists. On reopen, RE-ATTACH to a
  // job still running (poll it again) instead of discarding it — so "close + re-open to see
  // results" actually works. A fresh open (nothing running) sits on the config screen.
  useEffect(() => {
    if (!open) { stopPolling(); return; }
    if (phase === "scanning" && auditId) pollJob(auditId, onScanTick);
    else if (phase === "fixing" && fixId) pollJob(fixId, onFixTick);
    return stopPolling;
  }, [open]);

  // Live count preview while configuring.
  useEffect(() => {
    if (!open || phase !== "config") return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/bulk-operations/freight-audit/count`, {
          params: { created_from: from, created_to: to, seller_id: sellerId || undefined },
        });
        if (!cancelled) setCount(data);
      } catch { if (!cancelled) setCount(null); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, phase, from, to, sellerId]);

  const cancelJob = async (id) => {
    if (!id) return;
    try { await axios.post(`${API}/bulk-operations/freight-audit/cancel/${id}`); toast.message("Cancelling…"); }
    catch { /* best-effort; the task stops at its next iteration */ }
  };

  const startScan = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/bulk-operations/freight-audit/execute`, {
        created_from: from, created_to: to, seller_id: sellerId || null,
      });
      setAuditId(data.operation_id);
      setAudit({ status: "processing", total_orders: data.total_orders, processed_orders: 0, mismatch_count: 0 });
      setPhase("scanning");
      pollJob(data.operation_id, onScanTick);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the scan");
    } finally {
      setStarting(false);
    }
  };

  const toggle = (oid) => setSelected((prev) => {
    const n = new Set(prev); n.has(oid) ? n.delete(oid) : n.add(oid); return n;
  });

  const startFix = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await axios.post(`${API}/bulk-operations/freight-audit/fix`, {
        audit_id: auditId, order_ids: ids,
      });
      setFixId(data.operation_id);
      setFixDoc({ status: "processing", total_orders: data.total_orders, processed_orders: 0 });
      setPhase("fixing");
      pollJob(data.operation_id, onFixTick);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the fix");
    }
  };

  const discrepancies = audit?.discrepancies || [];
  const allSelected = discrepancies.length > 0 && selected.size === discrepancies.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" style={{ color: "#00A699" }} /> Check Freight Charges
          </DialogTitle>
          <DialogDescription>
            Re-pull live Delhivery freight for delivered / return-delivered orders and flag the ones
            whose seller charge no longer matches. Review, then fix the ones you select.
          </DialogDescription>
        </DialogHeader>

        {/* ---------- CONFIG ---------- */}
        {phase === "config" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From (order created)</Label>
                <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={to} min={from} max={today()} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Seller ID (optional)</Label>
              <Input placeholder="e.g. EDGS-011 — leave blank for all sellers" value={sellerId}
                     onChange={(e) => setSellerId(e.target.value.trim())} />
            </div>
            <div className="rounded-lg border bg-slate-50 p-3 text-sm flex items-center justify-between">
              <span className="text-slate-600">
                {count == null
                  ? "…"
                  : count.over_cap
                    ? `More than ${count.cap.toLocaleString("en-IN")} orders in range`
                    : `${count.count.toLocaleString("en-IN")} order(s) in range`}
              </span>
              <span className="text-xs text-slate-400">Scan is capped at {count?.cap ?? 1000}</span>
            </div>
            {count?.over_cap && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                Too many orders ({count.count.toLocaleString("en-IN")}). Narrow the date range or pick a seller
                (cap {count.cap}).
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={startScan} disabled={starting || !count || count.count === 0 || count.over_cap}
                      style={{ backgroundColor: "#00A699" }} className="hover:opacity-90" data-testid="start-freight-audit">
                {starting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                Start scan
              </Button>
            </div>
          </div>
        )}

        {/* ---------- SCANNING ---------- */}
        {phase === "scanning" && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#00A699" }} />
              Re-pulling Delhivery freight and comparing charges…
            </div>
            <ProgressBar done={audit?.processed_orders || 0} total={audit?.total_orders || 0} />
            <div className="text-sm">
              <span className="font-semibold text-red-600">{audit?.mismatch_count || 0}</span> discrepancies found so far
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">You can close this — the scan keeps running; re-open to see results.</p>
              <Button variant="outline" size="sm" onClick={() => cancelJob(auditId)} data-testid="cancel-freight-audit">Cancel scan</Button>
            </div>
          </div>
        )}

        {/* ---------- RESULTS ---------- */}
        {phase === "results" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">
                Scanned {audit?.successful_orders || 0} order(s) ·{" "}
                <span className="font-semibold text-red-600">{discrepancies.length}</span> discrepancies
                {audit?.skipped_orders ? ` · ${audit.skipped_orders} skipped` : ""}
                {audit?.failed_orders ? ` · ${audit.failed_orders} errors` : ""}
              </span>
            </div>

            {discrepancies.length === 0 ? (
              <>
                <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                  <CheckCircle2 className="w-5 h-5" /> No discrepancies — all seller charges match the current Delhivery freight.
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={resetToConfig}>New scan</Button>
                  <Button onClick={() => onOpenChange(false)} style={{ backgroundColor: "#00A699" }} className="hover:opacity-90">Close</Button>
                </div>
              </>
            ) : (
              <>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-slate-50 border-b">
                      <tr className="text-xs text-slate-500">
                        <th className="p-2 w-8">
                          <Checkbox checked={allSelected}
                                    onCheckedChange={() => setSelected(allSelected ? new Set() : new Set(discrepancies.map((d) => d.order_id)))} />
                        </th>
                        <th className="p-2 text-left font-medium">Order / LRN</th>
                        <th className="p-2 text-left font-medium">Seller</th>
                        <th className="p-2 text-right font-medium">Current</th>
                        <th className="p-2 text-right font-medium">New</th>
                        <th className="p-2 text-right font-medium">Wallet Adj.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discrepancies.map((d) => (
                        <tr key={d.order_id} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="p-2">
                            <Checkbox checked={selected.has(d.order_id)} onCheckedChange={() => toggle(d.order_id)} />
                          </td>
                          <td className="p-2">
                            <div className="font-medium text-slate-800">{d.order_number}</div>
                            <div className="text-xs text-slate-400">{d.lrn}{d.status === "RETURN_DELIVERED" ? " · RTO" : ""}</div>
                          </td>
                          <td className="p-2 text-slate-600">{d.seller_name || d.seller_id}</td>
                          <td className="p-2 text-right tabular-nums">₹{money(d.old_amount)}</td>
                          <td className="p-2 text-right tabular-nums font-medium">₹{money(d.new_amount)}</td>
                          <td className="p-2 text-right tabular-nums">
                            <span className={d.direction === "debit" ? "text-red-600" : "text-green-600"}>
                              {d.direction === "debit" ? "−" : "+"}₹{money(Math.abs(d.difference))}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-xs text-slate-500">{selected.size} selected</span>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={resetToConfig}>New scan</Button>
                    <Button onClick={startFix} disabled={selected.size === 0}
                            style={{ backgroundColor: "#00A699" }} className="hover:opacity-90" data-testid="fix-selected-freight">
                      Fix selected ({selected.size})
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-400">
                  Fixing applies the exact amount shown; any order whose freight changed since this scan is skipped and reported.
                </p>
              </>
            )}
          </div>
        )}

        {/* ---------- FIXING ---------- */}
        {phase === "fixing" && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#00A699" }} />
              Applying freight fixes and adjusting wallets…
            </div>
            <ProgressBar done={fixDoc?.processed_orders || 0} total={fixDoc?.total_orders || 0} />
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => cancelJob(fixId)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* ---------- DONE ---------- */}
        {phase === "done" && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                <div className="text-2xl font-bold text-green-700">{fixDoc?.successful_orders || 0}</div>
                <div className="text-xs text-green-700">Fixed</div>
              </div>
              <div className="rounded-lg bg-slate-50 border p-3">
                <div className="text-2xl font-bold text-slate-600">{fixDoc?.skipped_orders || 0}</div>
                <div className="text-xs text-slate-500">Skipped</div>
              </div>
              <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                <div className="text-2xl font-bold text-red-700">{fixDoc?.failed_orders || 0}</div>
                <div className="text-xs text-red-700">Failed</div>
              </div>
            </div>
            {Array.isArray(fixDoc?.results) && fixDoc.results.length > 0 && (
              <div className="overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <thead className="bg-slate-50 border-b sticky top-0">
                    <tr className="text-xs text-slate-500">
                      <th className="p-2 text-left font-medium">Order</th>
                      <th className="p-2 text-left font-medium">Result</th>
                      <th className="p-2 text-left font-medium">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fixDoc.results.map((r, i) => (
                      <tr key={r.order_id || i} className="border-b last:border-0">
                        <td className="p-2">{r.order_number || r.order_id}</td>
                        <td className="p-2">
                          <span className={
                            r.status === "success" ? "text-green-700" : r.status === "failed" ? "text-red-700" : "text-slate-500"
                          }>{r.status}</span>
                        </td>
                        <td className="p-2 text-slate-600 text-xs">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={resetToConfig}>New scan</Button>
              <Button onClick={() => onOpenChange(false)} style={{ backgroundColor: "#00A699" }} className="hover:opacity-90">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
